import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  alpha,
  LinearProgress,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Divider,
  Alert,
  CircularProgress,
  TextField,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import HomeIcon from '@mui/icons-material/Home';
import PeopleIcon from '@mui/icons-material/People';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BusinessIcon from '@mui/icons-material/Business';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import WarningIcon from '@mui/icons-material/Warning';
import PersonIcon from '@mui/icons-material/Person';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import RefreshIcon from '@mui/icons-material/Refresh';
import PieChartIcon from '@mui/icons-material/PieChart';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FilterListIcon from '@mui/icons-material/FilterList';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import {
  DashboardService,
  DashboardStats,
  PaymentDue,
  RevenueStats,
  UnitAnalytics,
  ExpiringLease,
  PaymentFilters,
  RevenueFilters,
  UnitAnalyticsFilters,
  ExpiringLeasesFilters,
  NewInstallmentsResponse,
  NewInstallmentsFilters
} from '../../services/dashboard-service';
import { LovService, LovItem } from '../../services/lov-service';
import ComplaintsDashboardWidget from '../../components/ComplaintsDashboardWidget';

/** ============ Animations ============ */
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;
const slideInLeft = keyframes`
  from { opacity: 0; transform: translateX(-30px); }
  to { opacity: 1; transform: translateX(0); }
`;
const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;
const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

/** ============ Theme-driven tokens (NO HEX) ============ */
const useTokens = () => {
  const theme = useTheme();
  const p = theme.palette;

  // Semantic roles mapped to palette
  const roles = {
    surfacePrimaryFrom: alpha(p.primary.main, 0.98),
    surfacePrimaryTo: alpha(p.primary.main, 0.03),

    surfaceInfoFrom: alpha(p.info.main, 0.98),
    surfaceInfoTo: alpha(p.info.main, 0.03),

    surfaceSuccessFrom: alpha(p.success.main, 0.98),
    surfaceSuccessTo: alpha(p.success.main, 0.03),

    white: p.common.white,
    black: p.common.black,

    // Statuses
    paid: p.success.main,
    pending: p.warning.main,
    overdue: p.error.main,

    occupied: p.success.main,
    vacant: p.warning.main,

    // Accents for charts/cards (cycle these)
    accents: [
      p.primary.main,
      p.warning.main,
      p.error.main,
      p.secondary.main,
      p.info.main,
      p.success.main,
    ],

    // Gradients
    kpiPrimary: (theme: any) => `linear-gradient(135deg, ${p.primary.main} 0%, ${p.primary.dark} 100%)`,
    kpiSecondary: (theme: any) => `linear-gradient(135deg, ${p.secondary.main} 0%, ${p.secondary.dark} 100%)`,
    kpiSuccess: (theme: any) => `linear-gradient(135deg, ${p.success.main} 0%, ${p.success.dark} 100%)`,
    kpiInfoToPrimary: (theme: any) => `linear-gradient(135deg, ${p.info.main} 0%, ${p.primary.main} 100%)`,

    headerStripe: `linear-gradient(90deg, ${p.primary.main} 0%, ${p.success.main} 50%, ${p.secondary.main} 100%)`,
  };

  const gradients = {
    // Generic soft card surface using theme hues
    soft(primaryFrom: string, primaryTo: string) {
      return `linear-gradient(135deg, ${primaryFrom} 0%, ${primaryTo} 100%)`;
    },
    kpi(g: string) {
      return `linear-gradient(135deg, ${g})`;
    },
  };

  // Utility for consistent subtle borders/shadows
  const outline = {
    onWhite: alpha(roles.white, 0.2),
    onPrimary: alpha(p.primary.main, 0.15),
    onInfo: alpha(p.info.main, 0.15),
  };

  return { theme, p, roles, gradients, outline };
};

/** ============ Styled components using theme tokens only ============ */
const DashboardContainer = styled(Box)({
  width: '100%',
  minHeight: '100vh',
  padding: '16px',
  animation: `${fadeIn} 0.6s ease-out`,
});

const PageHeader = styled(Box)(({ theme }) => {
  const { roles, gradients } = useTokens();
  return {
    marginBottom: 20,
    padding: 20,
    borderRadius: 16,
    background: gradients.soft(alpha(theme.palette.primary.main, 0.1), alpha(theme.palette.success.main, 0.08)),
    border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '3px',
      background: roles.headerStripe,
      backgroundSize: '200% 100%',
      animation: `${gradientShift} 3s ease infinite`,
    }
  };
});

const KPICard = styled(Card)<{ gradient: string }>(({ theme, gradient }) => ({
  height: '100%',
  borderRadius: 16,
  background: `linear-gradient(135deg, ${gradient})`,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.shadows[2],
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[4],
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: -50,
    right: -50,
    width: 150,
    height: 150,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.12)',
    transition: 'all 0.3s ease',
  },
  '&:hover::before': {
    top: -30,
    right: -30,
    width: 180,
    height: 180,
  },
}));

const ChartCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: 16,
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.shadows[1],
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[3],
  },
}));

const StatBox = styled(Box)(({ theme }) => ({
  padding: 12,
  borderRadius: 12,
  background: theme.palette.action.hover,
  border: `1px solid ${theme.palette.divider}`,
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  cursor: 'pointer',
  '&:hover': {
    transform: 'scale(1.02)',
    boxShadow: theme.shadows[1],
  },
}));

const FilterCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  background: theme.palette.background.paper,           // <-- white
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.shadows[1],
  marginBottom: 16,
  overflow: 'visible',
}));

const ScrollContainer = styled(Box)({
  display: 'flex',
  overflowX: 'auto',
  gap: 16,
  padding: '8px 4px',
  scrollBehavior: 'smooth',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': { display: 'none' }
});

const Dashboard: React.FC = () => {
  const { theme, p, roles, gradients } = useTokens();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [paymentsDue, setPaymentsDue] = useState<PaymentDue[]>([]);
  const [revenueStats, setRevenueStats] = useState<RevenueStats | null>(null);
  const [unitAnalytics, setUnitAnalytics] = useState<UnitAnalytics | null>(null);
  const [expiringLeases, setExpiringLeases] = useState<ExpiringLease[]>([]);
  const [newInstallments, setNewInstallments] = useState<NewInstallmentsResponse>({ items: [], totalCount: 0, pageNumber: 1, pageSize: 10, totalPages: 0 });

  const [loadingPayments, setLoadingPayments] = useState(false);
  const [loadingRevenue, setLoadingRevenue] = useState(false);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [loadingLeases, setLoadingLeases] = useState(false);
  const [loadingInstallments, setLoadingInstallments] = useState(false);

  const [paymentFilterOpen, setPaymentFilterOpen] = useState(false);
  const [revenueFilterOpen, setRevenueFilterOpen] = useState(false);
  const [unitFilterOpen, setUnitFilterOpen] = useState(false);
  const [leasesFilterOpen, setLeasesFilterOpen] = useState(false);

  const [paymentFilters, setPaymentFilters] = useState<PaymentFilters>({ days: 30, includeOverdue: true });
  const [revenueFilters, setRevenueFilters] = useState<RevenueFilters>({});
  const [unitFilters, setUnitFilters] = useState<UnitAnalyticsFilters>({ limit: 10 });
  const [leasesFilters, setLeasesFilters] = useState<ExpiringLeasesFilters>({ days: 30 });
  const [installmentsFilters, setInstallmentsFilters] = useState<NewInstallmentsFilters>({ pageNumber: 1, pageSize: 10 });

  const [projects, setProjects] = useState<LovItem[]>([]);
  const [properties, setProperties] = useState<LovItem[]>([]);
  const [loadingLov, setLoadingLov] = useState(false);

  const fetchDashboardData = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);
      const [stats, payments, revenue, analytics, leases] = await Promise.all([
        DashboardService.getStats(),
        DashboardService.getPaymentsDue({ days: 30, includeOverdue: true }),
        DashboardService.getRevenueStats({}),
        DashboardService.getUnitAnalytics({ limit: 10 }),
        DashboardService.getExpiringLeases({ days: 30 }),
      ]);
      setDashboardStats(stats);
      setPaymentsDue(payments);
      setRevenueStats(revenue);
      setUnitAnalytics(analytics);
      setExpiringLeases(leases);
    } catch (err) {
      console.error(err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPayments = useCallback(async () => {
    try {
      setLoadingPayments(true);
      const payments = await DashboardService.getPaymentsDue(paymentFilters);
      setPaymentsDue(payments);
    } finally {
      setLoadingPayments(false);
    }
  }, [paymentFilters]);

  const fetchRevenue = useCallback(async () => {
    try {
      setLoadingRevenue(true);
      const revenue = await DashboardService.getRevenueStats(revenueFilters);
      setRevenueStats(revenue);
    } finally {
      setLoadingRevenue(false);
    }
  }, [revenueFilters]);

  const fetchUnitAnalytics = useCallback(async () => {
    try {
      setLoadingUnits(true);
      const analytics = await DashboardService.getUnitAnalytics(unitFilters);
      setUnitAnalytics(analytics);
    } finally {
      setLoadingUnits(false);
    }
  }, [unitFilters]);

  const fetchExpiringLeases = useCallback(async () => {
    try {
      setLoadingLeases(true);
      const leases = await DashboardService.getExpiringLeases(leasesFilters);
      setExpiringLeases(leases);
    } finally {
      setLoadingLeases(false);
    }
  }, [leasesFilters]);

  const fetchNewInstallments = useCallback(async () => {
    try {
      setLoadingInstallments(true);
      const response = await DashboardService.getNewInstallments(installmentsFilters);
      setNewInstallments(response);
    } finally {
      setLoadingInstallments(false);
    }
  }, [installmentsFilters]);

  useEffect(() => { fetchDashboardData(); }, [fetchDashboardData]);
  useEffect(() => { fetchNewInstallments(); }, [fetchNewInstallments]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchDashboardData();
    setRefreshing(false);
  }, [fetchDashboardData]);

  const handleApplyPaymentFilters = useCallback(() => { fetchPayments(); setPaymentFilterOpen(false); }, [fetchPayments]);
  const handleApplyRevenueFilters = useCallback(() => { fetchRevenue(); setRevenueFilterOpen(false); }, [fetchRevenue]);
  const handleApplyUnitFilters = useCallback(() => { fetchUnitAnalytics(); setUnitFilterOpen(false); }, [fetchUnitAnalytics]);
  const handleApplyLeasesFilters = useCallback(() => { fetchExpiringLeases(); setLeasesFilterOpen(false); }, [fetchExpiringLeases]);

  const fetchLovData = useCallback(async () => {
    if (projects.length && properties.length) return;
    try {
      setLoadingLov(true);
      const [projectsData, propertiesData] = await Promise.all([
        LovService.getLovArray('projects?onlyActive=true'),
        LovService.getLovArray('properties?onlyActive=true')
      ]);
      setProjects(projectsData);
      setProperties(propertiesData);
    } finally {
      setLoadingLov(false);
    }
  }, [projects.length, properties.length]);

  useEffect(() => { if (unitFilterOpen) fetchLovData(); }, [unitFilterOpen, fetchLovData]);

  const kpiData = useMemo(() => {
    if (!dashboardStats) return [];
    return [
      {
        title: 'Total Properties',
        value: dashboardStats.totalProperties.toString(),
        change: '',
        isPositive: true,
        icon: <HomeIcon sx={{ fontSize: 40 }} />,
        gradient: `${p.primary.main} 0%, ${p.primary.dark} 100%`,
        color: roles.white,
      },
      {
        title: 'Active Projects',
        value: dashboardStats.totalProjects.toString(),
        change: '',
        isPositive: true,
        icon: <BusinessIcon sx={{ fontSize: 40 }} />,
        gradient: `${p.secondary.main} 0%, ${p.secondary.dark} 100%`,
        color: roles.white,
      },
      {
        title: 'Active Tenants',
        value: dashboardStats.totalActiveTenants.toString(),
        change: '',
        isPositive: true,
        icon: <PeopleIcon sx={{ fontSize: 40 }} />,
        gradient: `${p.success.main} 0%, ${p.success.dark} 100%`,
        color: roles.white,
      },
      {
        title: 'Target Revenue',
        value: `$${(dashboardStats.totalRevenue / 1000).toFixed(1)}K`,
        change: '',
        isPositive: true,
        icon: <AttachMoneyIcon sx={{ fontSize: 40 }} />,
        gradient: `${p.info.main} 0%, ${p.primary.main} 100%`,
        color: roles.white,
      },
    ];
  }, [dashboardStats, p, roles.white]);

  const occupancyData = useMemo(() => {
    if (!dashboardStats || dashboardStats.totalUnits === 0) return [];
    const occupiedPercentage = (dashboardStats.occupiedUnits / dashboardStats.totalUnits) * 100;
    const vacantPercentage = (dashboardStats.vacantUnits / dashboardStats.totalUnits) * 100;
    return [
      { name: 'Occupied', value: dashboardStats.occupiedUnits, percentage: occupiedPercentage, color: roles.occupied },
      { name: 'Vacant', value: dashboardStats.vacantUnits, percentage: vacantPercentage, color: roles.vacant }
    ];
  }, [dashboardStats, roles.occupied, roles.vacant]);

  const paymentStatusData = useMemo(() => {
    if (!paymentsDue.length) return [];
    const paid = paymentsDue.filter(pmt => pmt.status === 'Paid' || pmt.status === 'PartiallyPaid').length;
    const due = paymentsDue.filter(pmt => pmt.status === 'New' || pmt.status === 'Overdue' || pmt.daysUntilDue < 0).length;
    const total = paymentsDue.length || 1;
    return [
      { status: 'Paid', count: paid, percentage: (paid / total) * 100, color: roles.paid },
      { status: 'Due', count: due, percentage: (due / total) * 100, color: roles.overdue },
    ];
  }, [paymentsDue, roles.paid, roles.overdue]);

  const revenueByTypeData = useMemo(() => {
    if (!revenueStats?.revenueByType) return [];
    return revenueStats.revenueByType.slice(0, 4).map((item, idx) => ({
      category: item.feeTypeName,
      amount: item.totalAmount,
      color: roles.accents[idx % roles.accents.length],
    }));
  }, [revenueStats, roles.accents]);

  const allRevenueByTypeData = useMemo(() => {
    if (!revenueStats?.revenueByType) return [];
    return revenueStats.revenueByType.map((item, idx) => ({
      category: item.feeTypeName,
      amount: item.totalAmount,
      color: roles.accents[idx % roles.accents.length],
    }));
  }, [revenueStats, roles.accents]);

  const topProperties = useMemo(() => {
    if (!unitAnalytics?.topRevenueUnits) return [];
    return unitAnalytics.topRevenueUnits.map(u => ({
      name: u.projectName,
      unitNumber: u.unitNumber,
      occupancy: u.activeLeases > 0 ? 95 : 0, // same logic
      revenue: u.totalRevenue,
    }));
  }, [unitAnalytics]);

  const recentActivities = useMemo(() => {
    if (!paymentsDue.length) return [];
    return paymentsDue.map(payment => {
      const isOverdue = payment.daysUntilDue < 0;
      return {
        icon: isOverdue ? <WarningIcon sx={{ color: roles.pending }} /> : <EventAvailableIcon sx={{ color: roles.paid }} />,
        title: `Payment ${isOverdue ? 'Overdue' : 'Due'} - ${payment.unit.number}`,
        time: `${Math.abs(payment.daysUntilDue)} days ${isOverdue ? 'overdue' : 'remaining'}`,
        tenant: payment.tenant.name
      };
    });
  }, [paymentsDue, roles.paid, roles.pending]);

  const activitiesScrollRef = useRef<HTMLDivElement>(null);
  const propertiesScrollRef = useRef<HTMLDivElement>(null);
  const scroll = (ref: React.RefObject<HTMLDivElement>, direction: 'left' | 'right') => {
    if (ref.current) ref.current.scrollBy({ left: direction === 'left' ? -300 : 300, behavior: 'smooth' });
  };

  if (loading && !dashboardStats) {
    return (
      <DashboardContainer>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh', flexDirection: 'column', gap: 2 }}>
          <CircularProgress size={60} sx={{ color: p.primary.main }} />
          <Typography variant="h6" sx={{ color: 'text.secondary' }}>
            Loading dashboard data...
          </Typography>
        </Box>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer>
      {error && (
        <Alert
          severity="error"
          icon={<ErrorOutlineIcon />}
          onClose={() => setError(null)}
          sx={{ mb: 3 }}
        >
          {error}
        </Alert>
      )}

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {kpiData.map((kpi, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <KPICard gradient={kpi.gradient}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: 3,
                      background: alpha(roles.white, 0.2),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: roles.white,
                    }}
                  >
                    {kpi.icon}
                  </Box>
                  <Chip
                    icon={kpi.isPositive ? <TrendingUpIcon /> : <TrendingDownIcon />}
                    label={kpi.change}
                    size="small"
                    sx={{
                      background: kpi.isPositive ? alpha(p.success.main, 0.2) : alpha(p.error.main, 0.2),
                      color: kpi.isPositive ? p.success.light : p.error.main,
                      fontWeight: 700,
                      border: `1px solid ${kpi.isPositive ? alpha(p.success.main, 0.3) : alpha(p.error.main, 0.3)}`
                    }}
                  />
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 900, color: roles.white, mb: 0.5, textShadow: `0 2px 8px ${alpha(p.common.black, 0.2)}` }}>
                  {kpi.value}
                </Typography>
                <Typography variant="body2" sx={{ color: alpha(roles.white, 0.9), fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {kpi.title}
                </Typography>
              </CardContent>
            </KPICard>
          </Grid>
        ))}
      </Grid>

      {/* Revenue Trend & Occupancy */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Revenue Trend Chart */}
        <Grid item xs={12} md={8}>
          <ChartCard>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, background: `linear-gradient(135deg, ${p.primary.main} 0%, ${p.success.main} 100%)`, WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  Revenue Trend
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {loadingRevenue && <CircularProgress size={20} sx={{ color: p.primary.main }} />}
                  <Tooltip title="Filter Revenue">
                    <IconButton
                      size="small"
                      onClick={() => setRevenueFilterOpen(true)}
                      sx={{ background: alpha(p.primary.main, 0.1), '&:hover': { background: alpha(p.primary.main, 0.2) } }}
                    >
                      <FilterListIcon sx={{ color: p.primary.main, fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Bars */}
              <Box sx={{ position: 'relative', height: 180, mb: 2 }}>
                {revenueByTypeData.length > 0 ? (
                  <Box sx={{ display: 'flex', alignItems: 'flex-end', height: '100%', gap: 2, px: 2 }}>
                    {revenueByTypeData.map((item, index) => {
                      const maxAmount = Math.max(...revenueByTypeData.map(d => d.amount));
                      const heightPercentage = Math.max((item.amount / maxAmount) * 100, 15);
                      return (
                        <Box key={index} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end' }}>
                          <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 700, color: item.color }}>
                            ${(item.amount / 1000).toFixed(1)}K
                          </Typography>
                          <Tooltip title={`${item.category}: $${item.amount.toLocaleString()}`}>
                            <Box
                              sx={{
                                width: '100%',
                                minHeight: 30,
                                height: `${heightPercentage}%`,
                                background: `linear-gradient(180deg, ${item.color} 0%, ${alpha(item.color, 0.5)} 100%)`,
                                borderRadius: '8px 8px 0 0',
                                position: 'relative',
                                transition: 'all 0.3s ease',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                '&:hover': { transform: 'scaleY(1.05)', filter: 'brightness(1.1)' }
                              }}
                            />
                          </Tooltip>
                          <Typography variant="caption" sx={{ mt: 1, fontWeight: 600, fontSize: '0.7rem', textAlign: 'center' }}>
                            {item.category}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      No revenue data available
                    </Typography>
                  </Box>
                )}
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <StatBox>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: p.info.main, mb: 0.5 }}>
                      {revenueStats ? `$${(revenueStats.totalRevenue / 1000).toFixed(1)}K` : '$0'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                      Target Revenue
                    </Typography>
                  </StatBox>
                </Grid>
                <Grid item xs={4}>
                  <StatBox>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: p.success.main, mb: 0.5 }}>
                      {revenueStats ? `$${(revenueStats.totalCollected / 1000).toFixed(1)}K` : '$0'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                      Collected
                    </Typography>
                  </StatBox>
                </Grid>
                <Grid item xs={4}>
                  <StatBox>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: p.warning.main, mb: 0.5 }}>
                      {revenueStats ? `$${(revenueStats.totalOutstanding / 1000).toFixed(1)}K` : '$0'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                      Outstanding
                    </Typography>
                  </StatBox>
                </Grid>
              </Grid>

              {/* Revenue by Type Details */}
              {/* {revenueStats?.revenueByType && revenueStats.revenueByType.length > 0 && (
                <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${p.divider}` }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.secondary' }}>
                    Revenue Breakdown by Type
                  </Typography>
                  <Grid container spacing={1}>
                    {revenueStats.revenueByType.map((item, idx) => (
                      <Grid item xs={12} sm={6} md={3} key={item.feeTypeId}>
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            background: alpha(roles.accents[idx % roles.accents.length], 0.05),
                            border: `1px solid ${alpha(roles.accents[idx % roles.accents.length], 0.15)}`,
                          }}
                        >
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontWeight: 600, mb: 0.5 }}>
                            {item.feeTypeName}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Collected:</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: p.success.main }}>
                              ${(item.collectedAmount / 1000).toFixed(1)}K
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Outstanding:</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: p.warning.main }}>
                              ${(item.outstandingAmount / 1000).toFixed(1)}K
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )} */}
            </CardContent>
          </ChartCard>
        </Grid>

        {/* Occupancy */}
        <Grid item xs={12} md={4}>
          <ChartCard>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, background: `linear-gradient(135deg, ${p.secondary.main} 0%, ${p.secondary.dark} 100%)`, WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  Occupancy Status
                </Typography>
                <PieChartIcon sx={{ color: p.secondary.main, fontSize: 24 }} />
              </Box>

              {occupancyData.map((item, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {item.name}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: item.color }}>
                      {item.value} ({item.percentage.toFixed(1)}%)
                    </Typography>
                  </Box>
                  <Box sx={{ height: 10, borderRadius: 2, background: alpha(p.common.black, 0.05), overflow: 'hidden' }}>
                    <Box sx={{ height: '100%', width: `${item.percentage}%`, background: `linear-gradient(90deg, ${item.color} 0%, ${alpha(item.color, 0.7)} 100%)`, borderRadius: 2, transition: 'width 1s ease-in-out', boxShadow: `0 0 12px ${alpha(item.color, 0.4)}` }} />
                  </Box>
                </Box>
              ))}

              <Divider sx={{ my: 1.5 }} />

              <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: 2, background: gradients.soft(alpha(p.success.main, 0.1), alpha(p.primary.main, 0.05)) }}>
                <Typography variant="h4" sx={{ fontWeight: 900, background: `linear-gradient(135deg, ${p.success.main} 0%, ${p.primary.main} 100%)`, WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent', mb: 0.5 }}>
                  {dashboardStats && dashboardStats.totalUnits > 0
                    ? `${((dashboardStats.occupiedUnits / dashboardStats.totalUnits) * 100).toFixed(1)}%`
                    : '0%'}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  Overall Occupancy Rate
                </Typography>
              </Box>
            </CardContent>
          </ChartCard>
        </Grid>
      </Grid>

      {/* Stats, Lease, Payments */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Key Metrics */}
        <Grid item xs={12} md={4}>
          <ChartCard>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, background: `linear-gradient(135deg, ${p.primary.main} 0%, ${p.success.main} 100%)`, WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  Key Metrics
                </Typography>
                <PieChartIcon sx={{ color: p.primary.main, fontSize: 24 }} />
              </Box>

              <Box sx={{ position: 'relative', width: 140, height: 140, margin: '0 auto', mb: 2 }}>
                <Box sx={{ width: '100%', height: '100%', borderRadius: '50%', background: `linear-gradient(135deg, ${alpha(p.primary.main, 0.2)} 0%, ${alpha(p.success.main, 0.1)} 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', border: `3px solid ${alpha(p.primary.main, 0.3)}` }}>
                  <Typography variant="h5" sx={{ fontWeight: 900, color: p.primary.main }}>
                    {dashboardStats?.totalUnits || 0}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                    Total Units
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, borderRadius: 2, background: alpha(p.success.main, 0.05), border: `1px solid ${alpha(p.success.main, 0.1)}` }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', background: p.success.main }} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Active Leases
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: p.success.main }}>
                    {dashboardStats?.totalActiveLeases || 0}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, borderRadius: 2, background: alpha(p.warning.main, 0.05), border: `1px solid ${alpha(p.warning.main, 0.1)}` }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', background: p.warning.main }} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Due This Week
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: p.warning.main }}>
                    {dashboardStats?.paymentsDueThisWeek || 0}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, borderRadius: 2, background: alpha(p.error.main, 0.05), border: `1px solid ${alpha(p.error.main, 0.1)}` }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', background: p.error.main }} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Overdue
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: p.error.main }}>
                    {dashboardStats?.overduePaymentsCount || 0}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </ChartCard>
        </Grid>

        {/* Lease Status */}
        <Grid item xs={12} md={4}>
          <ChartCard>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, background: `linear-gradient(135deg, ${p.info.main} 0%, ${p.secondary.main} 100%)`, WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  Lease Status
                </Typography>
                <AssignmentIcon sx={{ color: p.info.main, fontSize: 24 }} />
              </Box>

              <Box sx={{ textAlign: 'center', mb: 2, p: 2, borderRadius: 2, background: `linear-gradient(135deg, ${alpha(p.primary.main, 0.1)} 0%, ${alpha(p.success.main, 0.05)} 100%)` }}>
                <Typography variant="h3" sx={{ fontWeight: 900, color: p.primary.main, mb: 0.5 }}>
                  {dashboardStats?.leasesExpiringWithin2Months || 0}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  Leases expiring within 2 months
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ p: 1.5, borderRadius: 2, background: alpha(p.success.main, 0.05), border: `1px solid ${alpha(p.success.main, 0.1)}` }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Active Leases
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: p.success.main }}>
                      {dashboardStats?.totalActiveLeases || 0}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={dashboardStats ? (dashboardStats.totalActiveLeases / (dashboardStats.totalUnits || 1)) * 100 : 0}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: alpha(p.common.black, 0.05),
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 4,
                        background: `linear-gradient(90deg, ${p.success.main} 0%, ${alpha(p.success.main, 0.7)} 100%)`,
                      }
                    }}
                  />
                </Box>
                <Box sx={{ p: 1.5, borderRadius: 2, background: alpha(p.warning.main, 0.05), border: `1px solid ${alpha(p.warning.main, 0.1)}` }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block' }}>
                    Average Lease Duration
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: p.warning.main }}>
                    {unitAnalytics ? `${unitAnalytics.averageLeaseDurationMonths.toFixed(1)} months` : 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </ChartCard>
        </Grid>

        {/* Payment Overview */}
        <Grid item xs={12} md={4}>
          <ChartCard>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, background: `linear-gradient(135deg, ${p.secondary.main} 0%, ${p.success.main} 100%)`, WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  Payment Overview
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {loadingPayments && <CircularProgress size={20} sx={{ color: p.secondary.main }} />}
                  <Tooltip title="Filter Payments">
                    <IconButton
                      size="small"
                      onClick={() => setPaymentFilterOpen(true)}
                      sx={{ background: alpha(p.secondary.main, 0.1), '&:hover': { background: alpha(p.secondary.main, 0.2) } }}
                    >
                      <FilterListIcon sx={{ color: p.secondary.main, fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {paymentStatusData.length > 0 ? (
                <>
                  {paymentStatusData.map((item, index) => (
                    <Box key={index} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {item.status}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: item.color }}>
                          {item.count} ({item.percentage.toFixed(1)}%)
                        </Typography>
                      </Box>
                      <Box sx={{ height: 10, borderRadius: 2, background: alpha(p.common.black, 0.05) }}>
                        <Box sx={{ height: '100%', width: `${item.percentage}%`, background: `linear-gradient(90deg, ${item.color} 0%, ${alpha(item.color, 0.7)} 100%)`, borderRadius: 2, transition: 'width 1s ease-in-out', boxShadow: `0 0 12px ${alpha(item.color, 0.4)}` }} />
                      </Box>
                    </Box>
                  ))}
                </>
              ) : (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    No payment data available
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1, p: 2, borderRadius: 2, background: alpha(p.success.main, 0.1), textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: p.success.main }}>
                    {paymentsDue.length > 0
                      ? `$${(paymentsDue.filter(p => p.status === 'Paid' || p.status === 'PartiallyPaid').reduce((sum, p) => sum + p.amount, 0) / 1000).toFixed(1)}K`
                      : '$0'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                    Paid (Filtered)
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, p: 2, borderRadius: 2, background: alpha(p.warning.main, 0.1), textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: p.warning.main }}>
                    {paymentsDue.length > 0
                      ? `$${(paymentsDue.filter(p => p.status === 'New' || p.status === 'Overdue' || p.daysUntilDue < 0).reduce((sum, p) => sum + p.amount, 0) / 1000).toFixed(1)}K`
                      : '$0'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                    Due (Filtered)
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </ChartCard>
        </Grid>
      </Grid>

      {/* Recent Activities & Expiring Leases */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Recent Payment Activities */}
        <Grid item xs={12} md={6}>
          <ChartCard sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, background: `linear-gradient(135deg, ${p.success.main} 0%, ${p.primary.main} 100%)`, WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  Recent Payments
                </Typography>
                <Button size="small" endIcon={<OpenInNewIcon />} sx={{ textTransform: 'none', fontWeight: 600, color: p.primary.main, '&:hover': { background: alpha(p.primary.main, 0.1) } }}>
                  View All
                </Button>
              </Box>

              {recentActivities.length > 0 ? (
                <Box sx={{ maxHeight: 400, overflowY: 'auto', pr: 1 }}>
                  {recentActivities.slice(0, 5).map((activity, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        gap: 1.5,
                        p: 1.5,
                        mb: 1.5,
                        borderRadius: 2,
                        background: alpha(p.primary.main, 0.03),
                        border: `1px solid ${alpha(p.primary.main, 0.1)}`,
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        '&:hover': { background: alpha(p.primary.main, 0.08), transform: 'translateX(4px)', boxShadow: `0 2px 8px ${alpha(p.primary.main, 0.15)}` }
                      }}
                    >
                      <Avatar sx={{ width: 40, height: 40, background: alpha(p.common.white, 0.5) }}>
                        {activity.icon}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.3, fontSize: '0.85rem' }}>
                          {activity.title}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                          {activity.time}
                        </Typography>
                        {activity.tenant && (
                          <Typography variant="caption" sx={{ color: 'text.primary', display: 'block', mt: 0.3, fontWeight: 600 }}>
                            {activity.tenant}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    No recent activities
                  </Typography>
                </Box>
              )}
            </CardContent>
          </ChartCard>
        </Grid>

        {/* Expiring Leases */}
        <Grid item xs={12} md={6}>
          <ChartCard sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, background: `linear-gradient(135deg, ${p.primary.main} 0%, ${p.success.main} 100%)`, WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  Expiring Leases
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    select
                    size="small"
                    value={leasesFilters.days || 30}
                    onChange={(e) => {
                      const newDays = Number(e.target.value);
                      setLeasesFilters({ ...leasesFilters, days: newDays });
                      DashboardService.getExpiringLeases({ days: newDays }).then(setExpiringLeases);
                    }}
                    sx={{ minWidth: 100 }}
                  >
                    <MenuItem value={7}>7 days</MenuItem>
                    <MenuItem value={14}>14 days</MenuItem>
                    <MenuItem value={30}>30 days</MenuItem>
                    <MenuItem value={60}>2 months</MenuItem>
                    <MenuItem value={90}>3 months</MenuItem>
                  </TextField>
                  {loadingLeases && <CircularProgress size={20} sx={{ color: p.primary.main }} />}
                </Box>
              </Box>

              {expiringLeases.length > 0 ? (
                <Box sx={{ maxHeight: 400, overflowY: 'auto', pr: 1 }}>
                  {expiringLeases.map((lease) => {
                    const daysUntilExpiry = Math.ceil((new Date(lease.endDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
                    const isExpiringSoon = daysUntilExpiry <= 7;
                    const chipBg = isExpiringSoon ? alpha(p.error.main, 0.15) : alpha(p.warning.main, 0.15);
                    const chipColor = isExpiringSoon ? p.error.main : p.warning.main;

                    return (
                      <Box
                        key={lease.id}
                        sx={{
                          p: 1.5,
                          mb: 1.5,
                          borderRadius: 2,
                          background: `linear-gradient(135deg, ${alpha(chipColor, 0.05)} 0%, ${alpha(p.secondary.main, 0.03)} 100%)`,
                          border: `1px solid ${alpha(chipColor, 0.15)}`,
                          transition: 'all 0.3s ease',
                          cursor: 'pointer',
                          '&:hover': {
                            transform: 'translateX(4px)',
                            boxShadow: `0 4px 12px ${alpha(chipColor, 0.2)}`,
                            border: `1px solid ${alpha(chipColor, 0.3)}`
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <HomeWorkIcon sx={{ color: chipColor, fontSize: 18 }} />
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              Unit {lease.unit.number}
                            </Typography>
                          </Box>
                          <Chip
                            label={`${daysUntilExpiry}d`}
                            size="small"
                            sx={{ background: chipBg, color: chipColor, fontWeight: 700, fontSize: '0.65rem', height: 20 }}
                          />
                        </Box>

                        <Box sx={{ mb: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                            {lease.tenant.fullName}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                            {lease.tenant.emails[0]}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1, borderTop: `1px solid ${p.divider}` }}>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {new Date(lease.endDate).toLocaleDateString()}
                          </Typography>
                          {lease.fees.length > 0 && (
                            <Typography variant="caption" sx={{ fontWeight: 700, color: p.info.main }}>
                              {lease.fees[0].currency} {lease.fees[0].value}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    No expiring leases found
                  </Typography>
                </Box>
              )}
            </CardContent>
          </ChartCard>
        </Grid>

        {/* Tenant Complaints Widget */}
        <Grid item xs={12} md={6}>
          <ComplaintsDashboardWidget />
        </Grid>
      </Grid>

      {/* Top Units */}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <ChartCard>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, background: `linear-gradient(135deg, ${p.primary.main} 0%, ${p.success.main} 100%)`, WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  Top Performing Units
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {loadingUnits && <CircularProgress size={20} sx={{ color: p.primary.main }} />}
                  <Tooltip title="Scroll Left">
                    <IconButton size="small" onClick={() => scroll(propertiesScrollRef, 'left')} sx={{ background: alpha(p.primary.main, 0.1), '&:hover': { background: alpha(p.primary.main, 0.2) } }}>
                      <ChevronLeftIcon sx={{ color: p.primary.main }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Scroll Right">
                    <IconButton size="small" onClick={() => scroll(propertiesScrollRef, 'right')} sx={{ background: alpha(p.primary.main, 0.1), '&:hover': { background: alpha(p.primary.main, 0.2) } }}>
                      <ChevronRightIcon sx={{ color: p.primary.main }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Filter Units">
                    <IconButton size="small" onClick={() => setUnitFilterOpen(true)} sx={{ background: alpha(p.primary.main, 0.1), '&:hover': { background: alpha(p.primary.main, 0.2) } }}>
                      <FilterListIcon sx={{ color: p.primary.main, fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                  <Button size="small" endIcon={<OpenInNewIcon />} sx={{ textTransform: 'none', fontWeight: 600, color: p.primary.main, '&:hover': { background: alpha(p.primary.main, 0.1) } }}>
                    View All
                  </Button>
                </Box>
              </Box>

              {topProperties.length > 0 ? (
                <ScrollContainer ref={propertiesScrollRef}>
                  {topProperties.map((property, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        background: `linear-gradient(135deg, ${alpha(p.primary.main, 0.05)} 0%, ${alpha(p.success.main, 0.03)} 100%)`,
                        border: `2px solid ${alpha(p.primary.main, 0.15)}`,
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        minWidth: 240,
                        maxWidth: 240,
                        '&:hover': {
                          transform: 'translateY(-4px) scale(1.01)',
                          boxShadow: `0 8px 24px ${alpha(p.primary.main, 0.2)}`,
                          border: `2px solid ${alpha(p.primary.main, 0.3)}`,
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                        <Avatar sx={{ width: 40, height: 40, background: `linear-gradient(135deg, ${p.primary.main} 0%, ${p.primary.dark} 100%)`, fontWeight: 700, fontSize: '0.9rem', color: roles.white }}>
                          {index + 1}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
                            {property.name}
                          </Typography>
                          {property.unitNumber && (
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                              Unit: {property.unitNumber}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      <Box sx={{ mb: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                            Occupancy
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: p.success.main }}>
                            {property.occupancy}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={property.occupancy}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: alpha(p.common.black, 0.05),
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 4,
                              background: `linear-gradient(90deg, ${p.success.main} 0%, ${alpha(p.success.main, 0.7)} 100%)`,
                            }
                          }}
                        />
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          <AttachMoneyIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                          Revenue
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: p.warning.main }}>
                          ${(property.revenue / 1000).toFixed(1)}K
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </ScrollContainer>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    No unit performance data available
                  </Typography>
                </Box>
              )}
            </CardContent>
          </ChartCard>
        </Grid>
      </Grid>

      {/* New Installments */}
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <ChartCard>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, background: `linear-gradient(135deg, ${p.primary.main} 0%, ${p.success.main} 100%)`, WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  New Installments
                </Typography>
                {loadingInstallments && <CircularProgress size={20} sx={{ color: p.primary.main }} />}
              </Box>

              {newInstallments.items.length > 0 ? (
                <>
                  <TableContainer component={Paper} sx={{ boxShadow: 'none', border: `1px solid ${p.divider}` }}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ background: alpha(p.primary.main, 0.05) }}>
                          <TableCell sx={{ fontWeight: 700 }}>Lease Ref</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Fee Type</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Billing Cycle</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Issued Date</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Due Date</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {newInstallments.items.map((installment) => {
                          const daysUntilDue = Math.ceil((new Date(installment.dueDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
                          const isDueSoon = daysUntilDue <= 7 && daysUntilDue >= 0;
                          const isOverdue = daysUntilDue < 0;

                          return (
                            <TableRow
                              key={installment.id}
                              sx={{
                                '&:hover': { background: alpha(p.primary.main, 0.02) },
                                transition: 'background 0.2s ease'
                              }}
                            >
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {installment.lease.refNumber}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                  {new Date(installment.lease.startDate).toLocaleDateString()} - {new Date(installment.lease.endDate).toLocaleDateString()}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {installment.fee.type}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={installment.fee.billingCycle}
                                  size="small"
                                  sx={{
                                    background: alpha(p.info.main, 0.1),
                                    color: p.info.main,
                                    fontWeight: 600,
                                    fontSize: '0.7rem'
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {new Date(installment.issuedAt).toLocaleDateString()}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: isOverdue ? p.error.main : isDueSoon ? p.warning.main : 'text.primary' }}>
                                  {new Date(installment.dueDate).toLocaleDateString()}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                  {isOverdue ? `${Math.abs(daysUntilDue)}d overdue` : `${daysUntilDue}d remaining`}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: p.success.main }}>
                                  {installment.currency}{installment.total.toLocaleString()}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={installment.status}
                                  size="small"
                                  sx={{
                                    background: installment.status === 'New' ? alpha(p.info.main, 0.15) : alpha(p.success.main, 0.15),
                                    color: installment.status === 'New' ? p.info.main : p.success.main,
                                    fontWeight: 700,
                                    fontSize: '0.7rem'
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={newInstallments.totalCount}
                    page={newInstallments.pageNumber - 1}
                    onPageChange={(event, newPage) => {
                      setInstallmentsFilters({ ...installmentsFilters, pageNumber: newPage + 1 });
                    }}
                    rowsPerPage={newInstallments.pageSize}
                    onRowsPerPageChange={(event) => {
                      setInstallmentsFilters({
                        ...installmentsFilters,
                        pageSize: parseInt(event.target.value, 10),
                        pageNumber: 1
                      });
                    }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    sx={{
                      borderTop: `1px solid ${p.divider}`,
                      '& .MuiTablePagination-select': {
                        color: p.primary.main
                      }
                    }}
                  />
                </>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    No new installments found
                  </Typography>
                </Box>
              )}
            </CardContent>
          </ChartCard>
        </Grid>
      </Grid>

      {/* ------ Filter Dialogs: only theme colors used ------ */}
      <Dialog open={paymentFilterOpen} onClose={() => setPaymentFilterOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ background: `linear-gradient(135deg, ${p.success.main} 0%, ${p.secondary.main} 100%)`, color: roles.white }}>
          Payment Filters
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField select label="Payment Due (Days)" size="small" fullWidth value={paymentFilters.days || 30}
                onChange={(e) => setPaymentFilters({ ...paymentFilters, days: Number(e.target.value) })}>
                <MenuItem value={7}>7 Days</MenuItem>
                <MenuItem value={14}>14 Days</MenuItem>
                <MenuItem value={30}>30 Days</MenuItem>
                <MenuItem value={60}>60 Days</MenuItem>
                <MenuItem value={90}>90 Days</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label="Include Overdue" size="small" fullWidth value={paymentFilters.includeOverdue ? 'yes' : 'no'}
                onChange={(e) => setPaymentFilters({ ...paymentFilters, includeOverdue: e.target.value === 'yes' })}>
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentFilterOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleApplyPaymentFilters}
            sx={{ background: `linear-gradient(135deg, ${p.success.main} 0%, ${p.secondary.main} 100%)` }}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={revenueFilterOpen} onClose={() => setRevenueFilterOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ background: `linear-gradient(135deg, ${p.primary.main} 0%, ${p.success.main} 100%)`, color: roles.white }}>
          Revenue Filters
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField label="Start Date" type="date" size="small" fullWidth value={revenueFilters.startDate || ''}
                onChange={(e) => setRevenueFilters({ ...revenueFilters, startDate: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="End Date" type="date" size="small" fullWidth value={revenueFilters.endDate || ''}
                onChange={(e) => setRevenueFilters({ ...revenueFilters, endDate: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevenueFilterOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleApplyRevenueFilters}
            sx={{ background: `linear-gradient(135deg, ${p.primary.main} 0%, ${p.success.main} 100%)` }}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={unitFilterOpen} onClose={() => setUnitFilterOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ background: `linear-gradient(135deg, ${p.primary.main} 0%, ${p.success.main} 100%)`, color: roles.white }}>
          Unit Analytics Filters
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField select label="Top Units to Show" size="small" fullWidth value={unitFilters.limit || 10}
                onChange={(e) => setUnitFilters({ ...unitFilters, limit: Number(e.target.value) })}>
                <MenuItem value={5}>Top 5</MenuItem>
                <MenuItem value={10}>Top 10</MenuItem>
                <MenuItem value={15}>Top 15</MenuItem>
                <MenuItem value={20}>Top 20</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Start Date" type="date" size="small" fullWidth value={unitFilters.startDate || ''}
                onChange={(e) => setUnitFilters({ ...unitFilters, startDate: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="End Date" type="date" size="small" fullWidth value={unitFilters.endDate || ''}
                onChange={(e) => setUnitFilters({ ...unitFilters, endDate: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Grid>
            {/* LOVs */}
            {/* ... unchanged inputs, colors are theme defaults ... */}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnitFilterOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleApplyUnitFilters}
            sx={{ background: `linear-gradient(135deg, ${p.primary.main} 0%, ${p.success.main} 100%)` }}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={leasesFilterOpen} onClose={() => setLeasesFilterOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ background: `linear-gradient(135deg, ${p.primary.main} 0%, ${p.success.main} 100%)`, color: roles.white }}>
          Expiring Leases Filters
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField select label="Expiring Within (Days)" size="small" fullWidth value={leasesFilters.days || 30}
                onChange={(e) => setLeasesFilters({ ...leasesFilters, days: Number(e.target.value) })}>
                <MenuItem value={7}>7 Days</MenuItem>
                <MenuItem value={14}>14 Days</MenuItem>
                <MenuItem value={30}>30 Days</MenuItem>
                <MenuItem value={60}>60 Days</MenuItem>
                <MenuItem value={90}>90 Days</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLeasesFilterOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleApplyLeasesFilters}
            sx={{ background: `linear-gradient(135deg, ${p.primary.main} 0%, ${p.success.main} 100%)` }}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardContainer>
  );
};

export default Dashboard;
